import React, { useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "helpers";
import { DateRange, DateRangePickerProps } from "./types";
import { PRESETS, buildRangeFromPreset, rangeLabel } from "./presets";

const WEEK_DAYS = ["PON", "UTO", "SRI", "ČET", "PET", "SUB", "NED"];
const GRID_CELLS = 42; // 6 tjedana
const MONTHS = [
  "Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
  "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac",
];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const sameDay = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange, align = "right" }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const [draftFrom, setDraftFrom] = useState<Date | null>(value.from);
  const [draftTo, setDraftTo] = useState<Date | null>(value.to);

  const initialView = value.from ?? value.to ?? new Date();
  const [viewYear, setViewYear] = useState(initialView.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialView.getMonth());

  // Sinkroniziraj nacrt s vrijednošću kad se popover otvori.
  useEffect(() => {
    if (open) {
      setDraftFrom(value.from);
      setDraftTo(value.to);
      const base = value.from ?? value.to ?? new Date();
      setViewYear(base.getFullYear());
      setViewMonth(base.getMonth());
    }
  }, [open, value.from, value.to]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Mreža počinje ponedjeljkom prije 1. u mjesecu i puni se danima susjednih mjeseci
  // do punih 6 tjedana — fiksna visina, pa popover ne poskakuje pri promjeni mjeseca.
  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const offset = (firstDay.getDay() + 6) % 7; // ponedjeljak = 0

    return Array.from({ length: GRID_CELLS }, (_, i) => {
      const date = new Date(viewYear, viewMonth, i - offset + 1);
      return { date, isOutside: date.getMonth() !== viewMonth };
    });
  }, [viewYear, viewMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const applyPreset = (presetId: DateRange["preset"]) => {
    onChange(buildRangeFromPreset(presetId));
    setOpen(false);
  };

  const handleDayClick = (day: Date) => {
    // Nema početka ili je raspon već kompletan -> počni novi odabir.
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(day);
      setDraftTo(null);
      return;
    }
    // Imamo početak, biramo kraj (uz swap ako je ranije).
    const a = draftFrom;
    const b = day;
    const from = a <= b ? a : b;
    const to = a <= b ? b : a;
    setDraftFrom(from);
    setDraftTo(to);
    onChange({ from: startOfDay(from), to: endOfDay(to), preset: "custom" });
  };

  const inRange = (day: Date) =>
    draftFrom && draftTo && day >= startOfDay(draftFrom) && day <= endOfDay(draftTo);
  const isEdge = (day: Date) => sameDay(day, draftFrom) || sameDay(day, draftTo);

  const handleClear = () => {
    setDraftFrom(null);
    setDraftTo(null);
    onChange(buildRangeFromPreset("all"));
  };

  const hint = !draftFrom
    ? "Klikni početni datum"
    : !draftTo
    ? "Klikni krajnji datum"
    : "Očisti";

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={classNames(
          "flex items-center gap-2 rounded-sm bg-white px-3 py-3 text-sm font-medium text-slate-700 ring-1 transition-all hover:ring-slate-300",
          open ? "ring-sky-400" : "ring-slate-200"
        )}
      >
        <img src="/media/icons/calendar.svg" alt="" className="w-4 h-4 opacity-70" />
        <span className="whitespace-nowrap">{rangeLabel(value)}</span>
        <svg className={classNames("w-4 h-4 text-slate-400 transition-transform", open && "rotate-180")} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div
          className={classNames(
            "absolute z-50 mt-2 w-80 rounded-sm bg-white p-4 ring-1 ring-slate-200 shadow-lg",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          {/* Zaglavlje */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth} className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100">
              ‹
            </button>
            <span className="text-sm font-semibold text-slate-800">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100">
              ›
            </button>
          </div>

          {/* Dani u tjednu */}
          <div className="grid grid-cols-7 gap-1 mb-1 text-center">
            {WEEK_DAYS.map((d) => (
              <div key={d} className="text-[11px] font-semibold text-slate-400">{d}</div>
            ))}
          </div>

          {/* Mreža dana */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {days.map(({ date, isOutside }, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleDayClick(date)}
                className={classNames(
                  "h-8 text-sm rounded-sm transition-colors",
                  isEdge(date)
                    ? "bg-sky-600 text-white font-semibold"
                    : inRange(date)
                    ? "bg-sky-100 text-sky-800"
                    : isOutside
                    ? "text-slate-300 hover:bg-sky-50"
                    : "text-slate-700 hover:bg-sky-50"
                )}
              >
                {date.getDate()}
              </button>
            ))}
          </div>

          {/* Hint + Očisti */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs">
            <span className="text-slate-500">{hint}</span>
            <button type="button" onClick={handleClear} className="text-slate-500 hover:text-slate-800 font-medium">
              Očisti
            </button>
          </div>

          {/* Brzi odabir */}
          <div className="mt-3">
            <div className="text-[11px] font-semibold text-slate-400 mb-2">BRZI ODABIR</div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  className={classNames(
                    "px-2.5 py-1 text-xs rounded-sm transition-colors",
                    value.preset === p.id
                      ? "ring-1 ring-sky-500 text-sky-700 bg-sky-50"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
